import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { supabase } from '../lib/supabase'

// Slide dimensions for perfect 1:1 fidelity
// Using ACTUAL canvas dimensions from Canvas.jsx for accurate positioning
const SLIDE_WIDTH = 800   // Current canvas width (4:3) - matches Canvas.jsx
const SLIDE_HEIGHT = 600  // Current canvas height (4:3) - matches Canvas.jsx

// Future upgrade option: Use 16:9 aspect ratio
// const SLIDE_WIDTH = 1280  // 16:9 HD width
// const SLIDE_HEIGHT = 720   // 16:9 HD height

/**
 * Export slides to PDF with perfect 1:1 fidelity using html2canvas → jsPDF approach
 * @param {Array} slides - Array of slide objects
 * @param {string} projectTitle - Project title for the PDF
 * @param {boolean} useActualSlides - Whether to capture actual slide elements or create new ones
 * @param {Object} userProfile - User profile to check subscription tier
 * @returns {Promise} Promise that resolves when PDF is generated and downloaded
 */
export const exportToPDF = async (slides, projectTitle = 'Presentation', useActualSlides = true, userProfile = null) => {
  if (!slides || slides.length === 0) {
    throw new Error('No slides to export')
  }

  // Create PDF with EXACT pixel dimensions (no scaling/margins)
  const pdf = new jsPDF({
    orientation: SLIDE_WIDTH > SLIDE_HEIGHT ? 'landscape' : 'portrait',
    unit: 'px',
    format: [SLIDE_WIDTH, SLIDE_HEIGHT], // Exact slide dimensions for 1:1 fidelity
  })

  // Add export mode class to body for clean rendering
  document.body.classList.add('export-mode')
  
  try {
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i]
      
      if (i > 0) {
        pdf.addPage()
      }

      let canvas
      if (useActualSlides) {
        canvas = await captureActualSlide(slide, i)
      }
      
      // Fallback to recreation if needed
      if (!canvas) {
        canvas = await createSlideCanvas(slide)
      }

      // Convert to optimized image (JPEG with high quality for smaller file size)
      const imgData = canvas.toDataURL('image/jpeg', 0.92) // 92% quality for optimal balance
      
      // Add image as FULL PAGE with proper scaling
      // If canvas was captured at higher resolution, it will be automatically scaled down
      pdf.addImage(imgData, 'JPEG', 0, 0, SLIDE_WIDTH, SLIDE_HEIGHT)
      
      // Add NuPitch watermark for free tier users only
      const shouldShowWatermark = !userProfile || userProfile.role === 'free'
      if (shouldShowWatermark) {
        addWatermark(pdf, SLIDE_WIDTH, SLIDE_HEIGHT)
      }
    }

    // Add metadata
    pdf.setProperties({
      title: projectTitle,
      subject: 'Generated by NuPitch',
      author: 'NuPitch',
      creator: 'NuPitch'
    })

    // Save with clean filename
    const fileName = `${projectTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.pdf`
    pdf.save(fileName)
    
  } finally {
    // Clean up export mode
    document.body.classList.remove('export-mode')
  }
}

/**
 * Capture the actual rendered slide from the editor with forced dimensions
 */
const captureActualSlide = async (slide, slideIndex) => {
  try {
    // Target the exact slide canvas with priority order
    const slideElement = document.querySelector(`[data-slide-id="${slide.id}"].slide-canvas`) ||
                        document.querySelector('.slide-canvas') ||
                        document.querySelector(`[data-slide-id="${slide.id}"]`)

    if (slideElement) {
      console.log(`📸 Capturing slide ${slideIndex + 1} with forced dimensions`)
      
      // Store original styles to restore later
      const originalOverflow = slideElement.style.overflow
      const originalPosition = slideElement.style.position
      
      // Force constrained dimensions during capture
      slideElement.style.overflow = 'hidden'
      slideElement.style.position = 'relative'
      
      try {
        // Get the element's position relative to viewport
        const rect = slideElement.getBoundingClientRect()
        
        // Capture at high resolution, then scale properly for PDF
        const highResCanvas = await html2canvas(slideElement, {
          width: SLIDE_WIDTH,
          height: SLIDE_HEIGHT,
          scale: 2, // 2x scale for high resolution (creates 1600x1200 canvas)
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          // Crop to exact dimensions
          x: 0,
          y: 0,
          scrollX: 0,
          scrollY: 0,
          ignoreElements: (element) => {
            // Ignore all UI overlay elements for clean capture
            return element.classList.contains('element-controls') ||
                   element.classList.contains('selection-box') ||
                   element.classList.contains('resize-handle') ||
                   element.classList.contains('ring-2') || // Selection rings
                   element.classList.contains('toolbar') ||
                   element.classList.contains('slide-panel') ||
                   element.classList.contains('properties-panel') ||
                   element.hasAttribute('data-ignore-export')
          }
        })
        
        // Create a scaled-down version for proper PDF dimensions
        return scaleCanvasForPDF(highResCanvas, SLIDE_WIDTH, SLIDE_HEIGHT)
      } finally {
        // Restore original styles
        slideElement.style.overflow = originalOverflow
        slideElement.style.position = originalPosition
      }
    }
  } catch (error) {
    console.warn(`⚠️ Failed to capture actual slide ${slideIndex + 1}:`, error)
  }
  
  return null
}

/**
 * Create a canvas by recreating the slide elements (enhanced fallback method)
 */
const createSlideCanvas = async (slide) => {
  // Create a temporary container with exact dimensions
  const slideContainer = document.createElement('div')
  slideContainer.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: ${SLIDE_WIDTH}px;
    height: ${SLIDE_HEIGHT}px;
    background: white;
    z-index: -1;
    font-family: system-ui, -apple-system, sans-serif;
    box-sizing: border-box;
  `
  
  // Apply slide background with forced constraints for exact dimensions
  if (slide.background) {
    if (slide.background.type === 'gradient') {
      slideContainer.style.background = slide.background.value
    } else if (slide.background.type === 'image') {
      // Force background image to be constrained to exact container dimensions
      slideContainer.style.backgroundImage = `url(${slide.background.url})`
      slideContainer.style.backgroundSize = slide.background.size || 'cover'
      slideContainer.style.backgroundPosition = slide.background.position || 'center'
      slideContainer.style.backgroundRepeat = 'no-repeat'
      slideContainer.style.backgroundClip = 'padding-box'
      slideContainer.style.overflow = 'hidden' // Crop background to container
    } else {
      slideContainer.style.backgroundColor = slide.background.value || '#ffffff'
    }
  }

  // Recreate elements with exact styling (sorted by z-index)
  if (slide.elements) {
    slide.elements
      .sort((a, b) => (a.style.zIndex || 0) - (b.style.zIndex || 0))
      .forEach(element => {
        const elementDiv = createElementDiv(element)
        slideContainer.appendChild(elementDiv)
      })
  }

  document.body.appendChild(slideContainer)

  try {
    // Wait for all images and fonts to load
    await Promise.all([
      ...Array.from(slideContainer.querySelectorAll('img')).map(img => 
        img.complete ? Promise.resolve() : new Promise(resolve => {
          img.onload = img.onerror = resolve
        })
      ),
      new Promise(resolve => setTimeout(resolve, 300)) // Font loading buffer
    ])

    // Capture the slide as high-resolution canvas
    const highResCanvas = await html2canvas(slideContainer, {
      width: SLIDE_WIDTH,
      height: SLIDE_HEIGHT,
      scale: 2, // 2x scale for high resolution (creates 1600x1200 canvas)
      useCORS: true,
      allowTaint: true,
      backgroundColor: null,
      logging: false
    })
    
    // Create a scaled-down version for proper PDF dimensions
    return scaleCanvasForPDF(highResCanvas, SLIDE_WIDTH, SLIDE_HEIGHT)
    
  } finally {
    // Clean up temporary container
    document.body.removeChild(slideContainer)
  }
}

/**
 * Create a DOM element for a slide element with exact styling
 */
const createElementDiv = (element) => {
  const elementDiv = document.createElement('div')
  elementDiv.style.cssText = `
    position: absolute;
    left: ${element.style.position.x}px;
    top: ${element.style.position.y}px;
    width: ${element.style.width}px;
    height: ${element.style.height}px;
    transform: rotate(${element.style.rotation || 0}deg);
    transform-origin: center center;
    box-sizing: border-box;
  `
  
  // Element-specific rendering matching Canvas.jsx
  switch (element.type) {
    case 'text':
      elementDiv.innerHTML = element.content || ''
      
      // Match Canvas.jsx text rendering exactly
      const textAlign = element.style.textAlign || 'left'
      const justifyContent = 
        textAlign === 'center' ? 'center' :
        textAlign === 'right' ? 'flex-end' : 'flex-start'
      
      // Check if text content might be multi-line or long
      const contentLength = (element.content || '').length
      const isLikelyMultiLine = contentLength > 50 || (element.content || '').includes('\n')
      
      elementDiv.style.cssText += `
        font-size: ${element.style.fontSize};
        font-weight: ${element.style.fontWeight || 'normal'};
        color: ${element.style.color || '#000000'};
        text-align: ${textAlign};
        font-family: ${element.style.fontFamily || 'system-ui, -apple-system, sans-serif'};
        line-height: ${element.style.lineHeight || '1.4'};
        display: flex;
        align-items: ${isLikelyMultiLine ? 'flex-start' : 'center'};
        justify-content: ${justifyContent};
        word-wrap: break-word;
        white-space: pre-wrap;
      `
      break
      
    case 'image':
      const img = document.createElement('img')
      img.src = element.src
      img.style.cssText = `
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: center;
        border-radius: ${element.style.borderRadius || '0px'};
      `
      elementDiv.appendChild(img)
      break
      
    case 'list':
      // Create ul element to match Canvas.jsx exactly
      const listContainer = document.createElement('ul')
      listContainer.style.cssText = `
        width: 100%;
        height: 100%;
        margin: 0;
        padding: 0;
        list-style: none;
        font-size: ${element.style.fontSize};
        color: ${element.style.color || '#000000'};
        font-family: ${element.style.fontFamily || 'system-ui, -apple-system, sans-serif'};
        line-height: ${element.style.lineHeight || '1.6'};
      `
      
      const items = Array.isArray(element.content) ? element.content : [element.content]
      items.forEach(item => {
        if (item) {
          const listItem = document.createElement('li')
          listItem.style.cssText = `
            margin-bottom: 0.5rem;
          `
          listItem.textContent = `• ${item}`
          listContainer.appendChild(listItem)
        }
      })
      
      elementDiv.appendChild(listContainer)
      break
      
    case 'shape':
      elementDiv.style.cssText += `
        background-color: ${element.style.backgroundColor || '#3b82f6'};
        border: ${element.style.border || 'none'};
        border-radius: ${element.style.shapeType === 'circle' ? '50%' : element.style.borderRadius || '0px'};
        clip-path: ${element.style.shapeType === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none'};
      `
      break
      
    case 'link':
      // Render links as text in PDF (links won't be clickable in PDF)
      elementDiv.innerHTML = element.content || ''
      
      // Match Canvas.jsx link rendering exactly
      const linkTextAlign = element.style.textAlign || 'left'
      const linkJustifyContent = 
        linkTextAlign === 'center' ? 'center' :
        linkTextAlign === 'right' ? 'flex-end' : 'flex-start'
      
      elementDiv.style.cssText += `
        font-size: ${element.style.fontSize};
        font-weight: ${element.style.fontWeight || 'normal'};
        color: ${element.style.color || '#000000'};
        text-align: ${linkTextAlign};
        font-family: ${element.style.fontFamily || 'system-ui, -apple-system, sans-serif'};
        line-height: ${element.style.lineHeight || '1.4'};
        display: flex;
        align-items: center;
        justify-content: ${linkJustifyContent};
        word-wrap: break-word;
        text-decoration: underline;
      `
      break

    case 'video':
      // For PDF export, show video placeholder/thumbnail
      const videoContainer = document.createElement('div')
      videoContainer.style.cssText = `
        width: 100%;
        height: 100%;
        background-color: #1a1a1a;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 24px;
        border-radius: ${element.style.borderRadius || '0px'};
      `
      videoContainer.innerHTML = '▶ Video'
      elementDiv.appendChild(videoContainer)
      break
      
    default:
      // Handle any other element types
      elementDiv.style.backgroundColor = '#f3f4f6'
      break
  }
  
  return elementDiv
}

/**
 * Scale a high-resolution canvas down to proper PDF dimensions
 * This maintains quality while ensuring correct positioning
 */
const scaleCanvasForPDF = (highResCanvas, targetWidth, targetHeight) => {
  // Create a new canvas with target dimensions
  const scaledCanvas = document.createElement('canvas')
  scaledCanvas.width = targetWidth
  scaledCanvas.height = targetHeight
  
  const ctx = scaledCanvas.getContext('2d')
  
  // Draw the high-res canvas scaled down to target size
  ctx.drawImage(
    highResCanvas, 
    0, 0, highResCanvas.width, highResCanvas.height, // source
    0, 0, targetWidth, targetHeight // destination
  )
  
  return scaledCanvas
}

/**
 * Add NuPitch watermark to PDF for free tier users
 */
const addWatermark = (pdf, width, height) => {
  pdf.setFontSize(9)
  pdf.setTextColor(128, 128, 128)
  
  const watermarkText = 'Created with NuPitch | https://usenupitch.com'
  
  // Calculate text width for proper positioning
  const textWidth = pdf.getTextWidth(watermarkText)
  
  // Position closer to the right edge with small margin
  const xPosition = width - textWidth - 10 // 10px margin from right edge
  const yPosition = height - 8 // 8px margin from bottom
  
  pdf.text(watermarkText, xPosition, yPosition)
}
